import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { NavigationService } from '@services/navigation.service';
import { TreatmentTypeService } from '@services/treatment-type.service';

@Injectable({
  providedIn: 'root',
})
export class GenderRestrictedGuard implements CanActivate {
  private router = inject(Router);
  private consultationStorageService = inject(ConsultationStorageService);
  private navigationService = inject(NavigationService);
  private treatmentTypeService = inject(TreatmentTypeService);

  public async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    if (!this.isGenderRestricted(state.url)) {
      return true;
    }

    const nextPageUrl = await this.navigationService.getConsultationRequestNextPageUrl(
      this.consultationStorageService.treatmentType,
      state.url,
    );

    return this.router.createUrlTree([nextPageUrl]);
  }

  /**
   * Determines if the current URL is restricted based on the patient's gender.
   *
   * @param {string} currentUrl the current URL to check for gender restrictions (query parameters are ignored)
   *
   * @returns {boolean} true if the current URL is restricted for the patient's gender, false otherwise
   */
  private isGenderRestricted(currentUrl: string): boolean {
    const restrictedGender = this.treatmentTypeService.getTreatmentType(this.consultationStorageService.treatmentType)
      ?.genderSettings.excludedForUpsells[currentUrl.split('?')[0]];

    if (!restrictedGender) {
      return false;
    }

    return this.consultationStorageService.patient?.gender === restrictedGender;
  }
}
